import { useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { findAll } from "../../Local/api-local";
import { findBySelectedYearAndLocalCodigo, findBySelectedYearAndLocalCodigoVersao2 } from "../api-cronograma";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import Loading from "../../UI/Loading";
import { mensagemDe } from "../cronogramaUtil";
import CronogramaLocalTable from "./CronogramaLocalTable";
import CronogramaLocalTableV2 from "./CronogramaLocalTableV2";

const LISTA = "lista";
const GRADE = "grade";

const CronogramaLocalContainer = () => {
  const [locais, setLocais] = useState([]);
  const [isLoadingLocais, setIsLoadingLocais] = useState(true);
  const [local, setLocal] = useState(null);
  const [visao, setVisao] = useState(LISTA);
  const [cronogramas, setCronogramas] = useState([]);
  const [isLoadingCronogramas, setIsLoadingCronogramas] = useState(false);
  const codLocal = local?.codigo;

  useEffect(() => {
    findAll()
      .then((data) => {
        if (!data) return;
        if (data.error) {
          errorMessage(mensagemDe(data));
        } else if (Array.isArray(data)) {
          setLocais([...data].sort((a, b) => (a.descricao ?? "").localeCompare(b.descricao ?? "", "pt-BR")));
        }
      })
      .finally(() => setIsLoadingLocais(false));
  }, []);

  useEffect(() => {
    setCronogramas([]);
    if (!codLocal) return undefined;
    let active = true;
    setIsLoadingCronogramas(true);
    const request =
      visao === GRADE ? findBySelectedYearAndLocalCodigoVersao2(codLocal) : findBySelectedYearAndLocalCodigo(codLocal);
    request
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(mensagemDe(data));
        } else if (Array.isArray(data)) {
          setCronogramas(data);
        }
      })
      .finally(() => {
        if (active) setIsLoadingCronogramas(false);
      });
    return () => {
      active = false;
    };
  }, [codLocal, visao]);

  const renderConteudo = () => {
    if (!local) {
      return (
        <Box
          sx={{
            py: 6,
            textAlign: "center",
            color: "text.secondary",
            border: 1,
            borderStyle: "dashed",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <MeetingRoomOutlinedIcon fontSize="large" />
          <Typography variant="body2" mt={1}>
            Selecione um local para ver a ocupação.
          </Typography>
        </Box>
      );
    }
    if (isLoadingCronogramas) {
      return (
        <Box py={6}>
          <Loading description="Carregando cronograma..." />
        </Box>
      );
    }
    return visao === GRADE ? (
      <CronogramaLocalTableV2 cronogramas={cronogramas} />
    ) : (
      <CronogramaLocalTable cronogramas={cronogramas} />
    );
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ md: "center" }}
        mb={2}
        className="no-print"
      >
        <Autocomplete
          options={locais}
          value={local}
          loading={isLoadingLocais}
          onChange={(event, value) => setLocal(value)}
          getOptionLabel={(option) => option.descricao ?? ""}
          isOptionEqualToValue={(option, value) => option.codigo === value.codigo}
          noOptionsText="Nenhum local encontrado"
          loadingText="Carregando locais..."
          renderInput={(params) => <TextField {...params} size="small" label="Local" />}
          sx={{ minWidth: 300, flexGrow: { md: 0 } }}
        />
        <ToggleButtonGroup size="small" exclusive value={visao} onChange={(event, value) => value && setVisao(value)}>
          <ToggleButton value={LISTA}>Lista</ToggleButton>
          <ToggleButton value={GRADE}>Grade semanal</ToggleButton>
        </ToggleButtonGroup>
        <Box flexGrow={1} />
        {local && visao === GRADE && (
          <Button variant="outlined" startIcon={<PrintOutlinedIcon />} onClick={() => window.print()}>
            Imprimir
          </Button>
        )}
      </Stack>

      {local && (
        <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
          {local.descricao}
        </Typography>
      )}

      {renderConteudo()}
    </Box>
  );
};

export default CronogramaLocalContainer;
